import { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, CircularProgress, Alert,
  TextField, InputAdornment, Tooltip, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import formatRut from '../../erp/formatRut';

export const EMPLOYEE_STATUS_COLORS = {
  draft: 'default',
  submitted: 'info',
  validated: 'warning',
  contracted: 'success',
};

const TAB_STATUSES = ['', 'draft', 'submitted', 'validated', 'contracted'];

/**
 * Fichas de colaborador (spec 3.5-3.6): tabs por estado del flujo
 * selección → BP → contrato.
 */
const ErpEmployeesLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organization, authToken, showAlert } = useStore.getState();

  const [records, setRecords] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // ficha a eliminar (dialogo)
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/erp/employee-records', {
        params: { organizationId: organization },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data.response?.status_response === 'OK') {
        setRecords(response.data.response.data || []);
      } else {
        setError(t('erp.employees.loadError'));
      }
    } catch (err) {
      console.error(err);
      setError(t('erp.employees.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organization) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      const response = await api.delete(`/erp/employee-records/${deleteTarget.uid}`, {
        params: { organizationId: organization },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data.response?.status_response === 'OK') {
        showAlert(t('erp.employees.deleteSuccess'), 'success');
        setDeleteTarget(null);
        load();
      }
    } catch (err) {
      console.error(err);
      const code = err.response?.data?.response?.code;
      showAlert(
        code === 'RECORD_HAS_CONTRACT'
          ? t('erp.employees.deleteHasContract')
          : t('erp.employees.deleteError'),
        'error'
      );
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    let list = records;
    const status = TAB_STATUSES[currentTab];
    if (status) list = list.filter((r) => r.status === status);

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter((r) => {
        const fullName = [r.personal?.names, r.personal?.firstLastName, r.personal?.secondLastName]
          .filter(Boolean).join(' ').toLowerCase();
        return fullName.includes(term) || r.personal?.rut?.toLowerCase().includes(term);
      });
    }

    setFiltered(list);
  }, [records, currentTab, searchTerm]);

  const fullName = (r) =>
    [r.personal?.names, r.personal?.firstLastName, r.personal?.secondLastName].filter(Boolean).join(' ') || '—';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>{t('erp.employees.title')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/erp/employees/new')}
          sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}>
          {t('erp.employees.new')}
        </Button>
      </Box>

      <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 2 }}>
        <Tab label={t('erp.employees.tabs.all')} sx={{ textTransform: 'none' }} />
        <Tab label={t('erp.employees.status.draft')} sx={{ textTransform: 'none' }} />
        <Tab label={t('erp.employees.status.submitted')} sx={{ textTransform: 'none' }} />
        <Tab label={t('erp.employees.status.validated')} sx={{ textTransform: 'none' }} />
        <Tab label={t('erp.employees.status.contracted')} sx={{ textTransform: 'none' }} />
      </Tabs>

      <TextField fullWidth size="small" placeholder={t('erp.employees.searchPlaceholder')}
        value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2, maxWidth: 420 }}
        InputProps={{
          startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchTerm('')}><ClearIcon fontSize="small" /></IconButton>
            </InputAdornment>
          ),
        }} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Alert severity="info">{t('erp.employees.empty')}</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('erp.employees.table.name')}</TableCell>
                <TableCell>{t('erp.employees.table.rut')}</TableCell>
                <TableCell>{t('erp.employees.table.source')}</TableCell>
                <TableCell>{t('erp.common.status')}</TableCell>
                <TableCell align="right">{t('erp.common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((record) => (
                <TableRow key={record.uid} hover sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/erp/employees/${record.uid}`)}>
                  <TableCell sx={{ fontWeight: 600 }}>{fullName(record)}</TableCell>
                  <TableCell>{formatRut(record.personal?.rut)}</TableCell>
                  <TableCell>
                    {record.applicationId
                      ? <Chip size="small" variant="outlined" label={t('erp.employees.sourceAts')} />
                      : <Chip size="small" variant="outlined" label={t('erp.employees.sourceManual')} />}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={t(`erp.employees.status.${record.status}`)}
                      color={EMPLOYEE_STATUS_COLORS[record.status] || 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('erp.common.view')}>
                      <IconButton size="small"
                        onClick={(e) => { e.stopPropagation(); navigate(`/erp/employees/${record.uid}`); }}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {record.status !== 'contracted' && (
                      <Tooltip title={t('erp.employees.deleteRecord')}>
                        <IconButton size="small" color="error"
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(record); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirmación de eliminación de ficha */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('erp.employees.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            {deleteTarget ? fullName(deleteTarget) : ''}
          </Typography>
          <Typography variant="body2">{t('erp.employees.deleteConfirmText')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: 'none' }}>
            {t('erp.common.cancel')}
          </Button>
          <Button variant="contained" color="error" disabled={deleting} onClick={handleDelete}
            sx={{ textTransform: 'none' }}>
            {t('erp.employees.deleteRecord')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErpEmployeesLayout;
